/**
 * Hook d'authentification professionnel
 * Utilise Redux + RTK Query de manière optimale
 */
import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { 
  useLoginMutation, 
  useLogoutMutation, 
  useGetProfileCoachQuery 
} from '../store';
import { 
  selectCurrentUser, 
  selectIsAuthenticated,
  logout as logoutAction 
} from '../store/slices/authSlice';

export const useAuthPro = () => {
  const dispatch = useAppDispatch();
  
  // Selectors Redux
  const user = useAppSelector(selectCurrentUser);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  
  // RTK Query hooks
  const [loginMutation, { isLoading: isLoggingIn }] = useLoginMutation();
  const [logoutMutation, { isLoading: isLoggingOut }] = useLogoutMutation();
  
  // Query du profil (skip si pas authentifié)
  const { 
    data: profileData, 
    isLoading: isLoadingProfile,
    refetch: refetchProfile 
  } = useGetProfileCoachQuery(undefined, {
    skip: !isAuthenticated,
  });

  /**
   * Fonction de connexion
   */
  const login = useCallback(async (email, password) => {
    try {
      const response = await loginMutation({ email, password }).unwrap();
      
      // Le authSlice gère automatiquement la mise à jour via extraReducers
      
      return response;
    } catch (error) {
      console.error('❌ Login failed:', error);
      throw error;
    }
  }, [loginMutation]);

  /**
   * Fonction de déconnexion
   */
  const logout = useCallback(async () => {
    try {
      await logoutMutation().unwrap();
      
    } catch (error) {
      console.error('⚠️ Logout API failed, clearing local state anyway');
    } finally {
      // Toujours nettoyer le state local
      dispatch(logoutAction());
      // La navigation sera gérée par le composant qui appelle logout
    }
  }, [logoutMutation, dispatch]);

  /**
   * Fonction pour rafraîchir le profil
   */
  const refreshProfile = useCallback(async () => {
    try {
      await refetchProfile();
    } catch (error) {
      console.error('❌ Profile refresh failed:', error);
    }
  }, [refetchProfile]);

  return {
    // État
    user: user || profileData,
    isAuthenticated,
    
    // Actions
    login,
    logout,
    refreshProfile,
    
    // États de chargement
    isLoading: isLoggingIn || isLoggingOut || isLoadingProfile,
    isLoggingIn,
    isLoggingOut,
    isLoadingProfile,
  };
};
